import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Checkbox, Icon } from 'Components';
import useClickOutside from '../../hooks/useClickOutside';
import {
	ALL_OPTION_LABELS,
	ALL_OPTION_VALUE,
	DEFAULT_ALL_OPTION_LABEL
} from '../../TrainingReports.constants';
import { FilterDefinition, FilterOption } from '../../TrainingReports.types';
import styles from './ReportFilters.module.scss';
import { useTranslation } from 'react-i18next';

interface FilterDropdownProps {
	definition: FilterDefinition;
	options: FilterOption[];
	selected: string[];
	onToggle: (value: string) => void;
	onSelect: (value: string) => void;
	onClear: () => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
	definition,
	options,
	selected,
	onToggle,
	onSelect,
	onClear
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useClickOutside(containerRef, isOpen, () => setIsOpen(false));
	const { t } = useTranslation();

	const { key, label, chipLabel, icon, multiple } = definition;
	const isActive = selected.length > 0;

	const selectedLabel = useMemo(() => {
		if (!isActive) return '';
		const match = options.find((option) => option.value === selected[0]);
		return match?.label ?? selected[0];
	}, [isActive, options, selected]);

	const handleOptionClick = (value: string) => {
		if (multiple) {
			onToggle(value);
			return;
		}

		onSelect(value);
		setIsOpen(false);
	};

	const handleClear = () => {
		onClear();
		setIsOpen(false);
	};

	return (
		<div className={styles.filter} ref={containerRef}>
			<div
				className={classNames(styles.control, {
					[styles['control--active']]: isActive,
					[styles['control--open']]: isOpen
				})}
			>
				<button
					type="button"
					className={styles.trigger}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					onClick={() => setIsOpen((open) => !open)}
				>
					{icon && (
						<Icon
							type={icon}
							role="img"
							className={styles.triggerIcon}
						/>
					)}
					<span className={styles.triggerLabel}>
						{isActive ? `${chipLabel}: ${t(selectedLabel)}` : t(label)}
					</span>
					{selected.length > 1 && (
						<span className={styles.counter}>
							{selected.length}
						</span>
					)}
					{!isActive && (
						<Icon
							type={isOpen ? 'chevronUp' : 'chevronDown'}
							role="img"
							className={styles.chevron}
						/>
					)}
				</button>

				{isActive && (
					<button
						type="button"
						className={styles.clearFilter}
						aria-label={`Clear ${label} filter`}
						onClick={handleClear}
					>
						<Icon type="close" role="img" />
					</button>
				)}
			</div>

			{isOpen && (
				<div className={styles.panel} role="listbox">
					{multiple ? (
						<>
							<div className={styles.panelHeader}>{t(label)}</div>
							<div className={styles.panelBody}>
								{options.map((option) => (
									<label
										key={option.value}
										className={styles.checkboxOption}
									>
										<Checkbox
											checked={selected.includes(
												option.value
											)}
											onChange={() =>
												handleOptionClick(option.value)
											}
										/>
										<span className={styles.optionLabel}>
											{t(option.label)}
										</span>
									</label>
								))}
							</div>
						</>
					) : (
						<div className={styles.panelBody}>
							<SingleSelectOption
								label={t(ALL_OPTION_LABELS[key] ?? DEFAULT_ALL_OPTION_LABEL)}
								isSelected={!isActive}
								onClick={() => handleOptionClick(ALL_OPTION_VALUE)}
							/>
							{options.map((option) => (
								<SingleSelectOption
									key={option.value}
									label={t(option.label)}
									isSelected={selected.includes(option.value)}
									onClick={() => handleOptionClick(option.value)}
								/>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

interface SingleSelectOptionProps {
	label: string;
	isSelected: boolean;
	onClick: () => void;
}

const SingleSelectOption: React.FC<SingleSelectOptionProps> = ({
	label,
	isSelected,
	onClick
}) => (
	<button
		type="button"
		role="option"
		aria-selected={isSelected}
		className={classNames(styles.option, {
			[styles['option--selected']]: isSelected
		})}
		onClick={onClick}
	>
		<span className={styles.optionLabel}>{label}</span>
		{isSelected && (
			<Icon type="check" role="img" className={styles.optionCheck} />
		)}
	</button>
);

export default FilterDropdown;
